import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPhoneVolume,
  faCircleInfo,
  faVideoCamera,
} from '@fortawesome/free-solid-svg-icons'
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import useAuth from '../../hooks/useAuth'
import moment from 'moment'
import { toast } from 'sonner'
import ClientAddressForm from '../../components/ClientAddressForm'
import PreferredContactNumber from '../../components/PreferredContactNumber'
import SupportingDocs from '../../components/SupportingDocs'
import Missing from '../Missing'

const EditGpConsultation = () => {
  const { auth, setShowLoader } = useAuth()
  const axios = useAxiosPrivate()
  const location = useLocation()
  const navigate = useNavigate()
  const [referral, setReferral] = useState([])
  const { booking } = useParams() // "id" will be "84638"
  const [reasonForBooking, setReasonForBooking] = useState('')
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [selectedReason, setSelectedReason] = useState('')
  const [reasonNotes, setReasonNotes] = useState('')
  const [clientAddressData, setClientAddressData] = useState([])
  const [phoneNumber, setPhoneNumber] = useState(null)
  const [consultationType, setConsultationType] = useState('')
  const [practitioner, setPractitioner] = useState({})
  const [notFound, setNotFound] = useState(false)
  const borderColour = '#FDD2B7'
  const iconColor = '#FDD2B7'

  function formatDate(datetimeStr, type = 'date') {
    const m = moment(datetimeStr)
    if (type === 'date') return m.format('dddd Do MMMM YYYY')
    if (type === 'time') return m.format('h:mm A')
    return ''
  }

  useEffect(() => {
    if (booking) {
      getBooking()
    }
  }, [booking])

  const getBooking = async () => {
    setShowLoader(true)
    try {
      const res = await axios.get(
        `/bookings/user/${auth?.user?.id}/booking/${booking}`
      )
      const errors = res?.data?.result?.errors || []
      let { data, included } = res?.data?.result || {
        data: null,
        included: null,
      }
      if (errors.length > 0 || !data) {
        setNotFound(true)
        setShowLoader(false)
        return
      }
      setClientAddressData(data?.attributes?.contactDetails?.address)
      setPhoneNumber(data.attributes.contactDetails.phoneNumber)
      setReasonForBooking(data.attributes.reasonForBooking)
      data = Array.isArray(data) ? data : [data]
      included = Array.isArray(included) ? included : [included]
      const combined = data.map((item, index) => ({
        data: item,
        included: included || null,
      }))
      setPractitioner(combined[0].included[1].attributes)
      setConsultationType(combined[0].data.attributes.consultationType)
      setReferral(combined)
      setShowLoader(false)
    } catch (error) {
      console.error('Error fetching bookings:', error)
      if (error?.response?.status === 404) {
        setNotFound(true)
      }
      setShowLoader(false)
    }
  }

  const updateBooking = async () => {
    setShowLoader(true)
    try {
      await axios.post(
        `/bookings/user/${auth?.user?.id}/booking/${booking}/update`,
        {
          user_id: auth?.user?.id,
          address: clientAddressData,
          phoneNumber: phoneNumber,
          reasonForBooking: reasonForBooking,
        }
      )
      setShowLoader(false)
      navigate('/')
      toast.success('Booking updated')
    } catch (error) {
      toast.error('Failed to update booking, please try again')
      setShowLoader(false)
    }
  }

  const lapsed =
    moment(referral[0]?.included[0]?.attributes.start).diff(moment()) < 0

  return notFound ? (
    <Missing />
  ) : (
    <section className="max-w-4xl mx-auto lg:p-6 text-portal-dark-blue">
      <form className="w-full mx-auto lg:p-6 overflow-y-scroll">
        {/* Header Section */}
        <div className="lg:px-4 py-6 ">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-[#979EF4] w-14 h-14 rounded-xl flex items-center justify-center">
                <FontAwesomeIcon
                  icon={
                    consultationType === 'Phone' ? faPhoneVolume : faVideoCamera
                  }
                  className="text-white text-xl"
                />
              </div>
              <div>
                <h2 className="text-xl lg:text-2xl font-semibold text-[#41264B]">
                  You {lapsed ? 'had' : 'have'} a {consultationType} call with a
                  GP Clinician
                </h2>
              </div>
            </div>
          </div>
        </div>

        <section className="lg:p-6 flex flex-col gap-6 h-full text-portal-dark-blue">
          {/* Booking Summary Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b">
                  <td className="px-4 py-3 font-semibold">Date and time</td>
                  <td className="px-4 py-3">
                    {formatDate(
                      referral[0]?.included[0]?.attributes?.start,
                      'date'
                    )}{' '}
                    {formatDate(
                      referral[0]?.included[0]?.attributes?.start,
                      'time'
                    )}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-3 font-semibold">Patient</td>
                  <td className="px-4 py-3">{auth?.user?.name}</td>
                </tr>
                <tr className="border-b">
                  <td className="px-4 py-3 font-semibold">
                    HealthHero Clinician
                  </td>
                  <td className="px-4 py-3">
                    {practitioner?.firstName} {practitioner.lastName}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Reason for booking */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Reason for booking</h2>
            <label className="block text-sm text-gray-600 mb-1">
              Symptoms of concern
            </label>
            <textarea
              rows={5}
              value={reasonForBooking}
              onChange={(e) => setReasonForBooking(e.target.value)}
              className="w-full border rounded-md p-3 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Please briefly describe your reason for booking and symptoms of concern."
            />
          </div>

          {/* Location */}
          {consultationType === 'Phone' && (
            <>
              <div>
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
                  Your location during consultation{' '}
                  <FontAwesomeIcon icon={faCircleInfo} />
                </h2>
                <p className="text-sm text-gray-600 mb-2">
                  Please enter a UK postcode to look up your address. If you
                  will be in a different country, please enter the address
                  manually.
                </p>
                <ClientAddressForm
                  clientAddressData={clientAddressData}
                  setClientAddressData={setClientAddressData}
                />
              </div>

              <div>
                {/* Phone number */}
                <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
                  Preferred Contact Number
                </h2>
                <PreferredContactNumber
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                />
              </div>
            </>
          )}

          {/* Documents */}
          <div className="mt-2">
            <SupportingDocs
              id={booking}
              isBooked={true}
              uploadUrl="/appointments/supportingDocs"
              deleteUrl="/appointments/supportingDocs/delete"
            />
          </div>

          {!lapsed && (
            <div className="flex flex-col mt-4 text-left items-center">
              <p>
                If your situation has changed, and you no longer need the
                consultation, click the button below.
              </p>
              <button
                type="button"
                className="w-1/2 bg-rose-500 text-white border border-gray-300 rounded-md py-2 hover:bg-rose-400 transition mt-4"
                onClick={() => setShowCancelModal(true)}
              >
                Cancel Booking
              </button>
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold mb-2">
              Need to speak to someone?
            </h2>
            <p className="text-sm text-gray-700 mb-1">
              Talk to our customer service team on:
            </p>
            <p className="text-sm font-semibold underline text-portal-medium mb-4">
              {/* 01773 814403 */}
              0123 456 7890
            </p>
            <p className="text-sm text-gray-700 mb-1">Or email us at:</p>
            <p className="text-sm font-semibold underline text-portal-medium pb-4">
              {/* nurse@uk-sas.co.uk */}
              gppatient@healthhero.com.
            </p>
          </div>

          <hr />
          {!lapsed && (
            <div className="flex flex-col w-full pt-2 items-center">
              <button
                type="button"
                className="w-1/2 bg-hub-primary text-white border border-gray-300 rounded-md py-2 hover:bg-hub-primary-hover transition"
                onClick={() => updateBooking()}
              >
                Save Changes
              </button>
            </div>
          )}
        </section>
      </form>

      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6">
            <div className="flex justify-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="2 2 20 20"
                className="w-12 h-12 text-rose-500"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10
                                    10-4.48 10-10S17.52 2 12 2zm0 11c-.55 0-1-.45-1-1V8c0-.55.45-1 
                                    1-1s1 .45 1 1v4c0 .55-.45 1-1 1zm1 4h-2v-2h2v2z"
                />
              </svg>
            </div>
            <p className="text-2xl font-bold text-center mb-6">
              Are you sure you want to cancel your booking?
            </p>
            <form
              className="w-full"
              onSubmit={async (e) => {
                e.preventDefault()
                if (!selectedReason) return
                setIsCancelling(true)
                try {
                  await axios.post(
                    `/bookings/user/${auth?.user?.id}/booking/${booking}/cancel`,
                    {
                      reason: selectedReason,
                      notes: reasonNotes,
                    }
                  )
                  toast.success(`Booking Cancelled ${booking}`)
                  navigate('/')
                } catch (error) {
                  console.error('Cancellation failed', error)
                } finally {
                  setIsCancelling(false)
                  setShowCancelModal(false)
                }
              }}
            >
              <h3
                id="cancel-consultation-type-title"
                className="text-base font-light mb-3"
              >
                Please select your reason for cancellation:
              </h3>
              <div
                role="radiogroup"
                aria-labelledby="cancel-consultation-type-title"
                className="space-y-3 mb-4"
              >
                {[
                  { id: 'NoLongerRelevant', label: 'No Longer Relevant' },
                  { id: 'UnableToAttend', label: 'Unable To Attend' },
                  { id: 'BookedInError', label: 'Booked In Error' },
                  { id: 'Other', label: 'Other' },
                ].map((option) => (
                  <label
                    key={option.id}
                    className="flex justify-between items-center border rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-50"
                  >
                    <span className="text-sm">{option.label}</span>
                    <input
                      id={option.id}
                      type="radio"
                      name="cancellationReason"
                      value={option.id}
                      checked={selectedReason === option.id}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="form-radio text-rose-500"
                    />
                  </label>
                ))}
              </div>
              {selectedReason === 'Other' && (
                <textarea
                  name="cancellationReasonNotes"
                  placeholder="If you selected ‘Other’, please specify."
                  value={reasonNotes}
                  onChange={(e) => setReasonNotes(e.target.value)}
                  className="w-full min-h-[100px] border rounded-lg p-3 mb-4 text-base focus:outline-none focus:ring-2 focus:ring-rose-400"
                />
              )}
              <div className="flex flex-col sm:flex-row justify-center gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCancelModal(false)}
                  className="bg-white border-2 border-[#E7CDDB] text-[#41264B] font-medium px-5 py-1.5 rounded-md hover:bg-[#E7CDDB] transition"
                >
                  No, take me back
                </button>
                <button
                  type="submit"
                  disabled={!selectedReason || isCancelling}
                  className="bg-[#E7CDDB] border-2 border-[#E7CDDB] text-[#41264B] font-medium px-5 py-1.5 rounded-md hover:bg-white transition"
                >
                  {isCancelling ? 'Cancelling...' : 'Yes, cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  )
}

export default EditGpConsultation
